import _ from 'lodash';
import turfBBox from '@turf/bbox';

// The whole US the country/overview frames: contiguous states plus the full Alaska landmass
// (to ~71.5°N), Hawaii and Puerto Rico, so the overview shows the entire country (like the markets
// map) rather than just the lower 48. Flat [west, south, east, north].
export const US_OVERVIEW_BOUNDS = [-172.0, 17.5, -66.0, 71.5];

// Padding (px) kept between fitted content and the map edge on overview-level fits.
export const MAP_EDGE_PADDING = 60;

// Expand a LngLatBounds outward by `factor` on each axis and return [[w,s],[e,n]] for setMaxBounds.
// Used for "fit then lock": after the whole-US overview is framed we fence the camera to that
// actual view (plus a little slack) so the fence adapts to the window shape instead of a static box
// clipping the US on short/wide windows. Latitude clamped to Mercator limits.
export function padBounds(bounds, factor = 0.15) {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  const dLng = (ne.lng - sw.lng) * factor;
  const dLat = (ne.lat - sw.lat) * factor;
  return [
    [sw.lng - dLng, Math.max(sw.lat - dLat, -85.051129)],
    [ne.lng + dLng, Math.min(ne.lat + dLat, 85.051129)],
  ];
}

export function prepareOverlayGeometry(overlaysData, skipOverlayCheck = false) {
  let bounds = [];
  const overlays = [];
  const featureCollection = {type: 'FeatureCollection', features: []};
  _.each(overlaysData, (overlay) => {
    const overlayType = _.get(overlay, 'properties.overlay_type');
    if (overlayType !== 'folio_by_id') {
      overlays.push(overlay);
    }
    if (overlayType === 'place' || skipOverlayCheck) {
      featureCollection.features.push({
        type: 'Feature',
        properties: {},
        geometry: overlay,
      });
    }
  });
  if (featureCollection.features.length) {
    bounds = turfBBox(featureCollection);
  }
  return {bounds, overlays};
}

export function fitBounds(map, bounds, leftOffset = 20, edgePadding = 20) {
  try {
    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      {
        padding: {
          bottom: edgePadding,
          // Keep content clear of the left menu overlay (leftOffset) and honour the edge padding.
          left: Math.max(leftOffset, edgePadding),
          right: edgePadding,
          top: edgePadding,
        },
        linear: true,
        maxZoom: 18,
        duration: 0,
      },
    );
  }
  catch (e) {
    console.error('Wrong bounds.', {e});
  }
}

export function leftPanelWidth() {
  const leftPanel = document.querySelector('.left-overlay-map');
  return leftPanel ? parseInt(window.getComputedStyle(leftPanel).width, 10) : 0;
}

// Frame the whole US and fence the camera to that fitted view (fit-then-lock). Uses a fixed
// symmetric padding (not the left-menu width): the menu width is 0 at map init but non-zero once
// expanded, so a panel-based offset made the framing differ between the initial load and navigating
// back to the country level. The overview already has ocean/Hawaii on its western side where the
// menu sits, so a symmetric fit keeps the US clear of the menu while staying identical every time.
// The fence is reset first so the fit is free (never clamped by a stale fence).
export function frameUsOverview(map) {
  if (!map) {
    return;
  }
  // Re-measure the container so the fit runs against the settled size, not a transient one.
  map.resize();
  map.setMaxBounds(null);
  map.setMinZoom(0);
  fitBounds(map, US_OVERVIEW_BOUNDS, MAP_EDGE_PADDING, MAP_EDGE_PADDING);
  map.setMaxBounds(padBounds(map.getBounds()));
  map.setMinZoom(map.getZoom());
}
